// Catalog.ID Client — Entropy Ceremony (start/stop credential generator)
// ============================================================================
// The finalize-at-print UI that mints the FINAL password, supersecret and XRPL
// master key (registration-restructure §4 + §13.1), porting the legacy
// mailsecret SecretKeySetup pattern: Start makes the displayed credentials
// cycle visibly (50 ms), Stop freezes them. Click timings and pointer movement
// feed the entropy pool; the final values derive from SHA-512(CSPRNG seed ‖
// pool digest) through the same rejection samplers as plain generation
// (crypto/entropy.ts — `drawCeremonyCredentials` owns the draw ORDER, which is
// golden-vector pinned).
//
// The cycling display values are throwaway eye candy (plain generation); only
// the values produced at Stop are real. Stopping again after a restart derives
// fresh values from a fresh seed + the grown pool.

import { createElement } from '@catalog-shell';
import {
    createEntropyPool,
    deriveCeremonyMaterial,
    drawCeremonyCredentials,
} from '../crypto/entropy';
import { generatePassword } from '../crypto/credentials';
import { generateSupersecret } from '../crypto/supersecret';
import { deriveXrplAddress, generateXrplSeedEntropy } from '../crypto/xrpl';

export interface EntropyCeremonyResult {
    password: string;
    supersecret: string;
    /** 16 bytes of XRPL family-seed entropy (round 6, §13.1) — becomes the
     *  `xrpl_master` bag entry at finalize, and the address shown here is the
     *  one the XRPL Master Key sheet prints. */
    xrplSeedEntropy: Uint8Array;
    /** 20 bytes of Authenticator Key (TOTP) secret (round 8, §15.1) — stored on
     *  the node at finalize and printed on the kit's Authenticator Key sheet.
     *  Deliberately NOT shown as a cycling row: the participation guarantee is
     *  the pool, and the sheet shows the value afterwards, so the ceremony UI is
     *  unchanged. */
    totpSecret: Uint8Array;
    /** Present when the ceremony also mints an organization supersecret
     *  (org registration — drawn from the same stream, after the XRPL bytes). */
    orgSupersecret?: string;
    /** The account's bitcash wallet key, compact form (registration-wallets
     *  §4.9). Drawn LAST, and only when a minter was supplied — so a ceremony
     *  without a minter draws exactly the values a pre-round-10 ceremony drew,
     *  byte for byte.
     *
     *  NOT shown as a cycling row, like the Authenticator Key and for the same
     *  reason: the participation guarantee is the pool, and the value appears
     *  on its own kit sheet afterwards. */
    bitcashWalletKey?: string;
    /** Org ceremony only: the FOUNDING DELEGATE's wallet (drawn first of the
     *  two) and the ORGANIZATION's (drawn second) — §4.13. */
    founderBitcashWalletKey?: string;
    orgBitcashWalletKey?: string;
}

export interface EntropyCeremonyConfig {
    /** Fired with the frozen credentials after Stop, and with null whenever
     *  the generator starts cycling again (result no longer valid). */
    onReady: (result: EntropyCeremonyResult | null) => void;
    /** Org registration: additionally mint (and display) the organization
     *  supersecret from the same ceremony. */
    includeOrgSupersecret?: boolean;
    /**
     * Compose one bitcash wallet key out of this ceremony's own byte stream
     * (registration-wallets §4.9):
     *
     *     next => generateWalletKeyFrom(next, routingPrefix, TYPE_CASH)
     *
     * INJECTED rather than imported: `crypto/entropy.ts` must not import
     * `crypto/bitcash.ts` (parent plan §13.1's cycle trap). The wizard knows
     * the routing prefix (the routing seam, docs/cid-governance.md M13), which
     * is the only thing the composition needs beyond the stream.
     *
     * Absent ⇒ no wallet is drawn and not one byte of the stream is consumed.
     */
    mintWalletKey?: (nextByte: () => number) => string;
    /** Row labels — org registration labels the first three as delegate
     *  credentials. Defaults: 'Password' / 'Supersecret' / 'XRPL address'. */
    passwordLabel?: string;
    supersecretLabel?: string;
    xrplLabel?: string;
}

export function createEntropyCeremony(config: EntropyCeremonyConfig): HTMLElement {
    const container = createElement('div', { className: 'cid-entropy-ceremony' });

    container.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'Your password, your supersecret and your XRPL master key are generated now, '
            + 'with your participation: press Start, let the values cycle, and press Stop whenever '
            + 'you choose. The exact moments you click — and your mouse movement in between — feed '
            + 'into the result, so these values did not exist before this moment.',
    }));

    // One line under each row saying what it is FOR (staging test 2026-10-15:
    // "what is a supersecret, what is an XRPL master key"). Worded for every
    // wizard that draws a ceremony — the personal one, the organization's
    // founding delegate, an invited delegate — so no line promises a recovery
    // path or a kit page that one of them lacks.
    const passwordRow = credentialRow(config.passwordLabel ?? 'Password',
        'What you type to sign in, together with your Catalog.ID.');
    const supersecretRow = credentialRow(config.supersecretLabel ?? 'Supersecret',
        'The master key to your account: it encrypts all your keys. It is never stored anywhere — '
            + 'the printed document set holds the only copy.');
    const xrplRow = credentialRow(config.xrplLabel ?? 'XRPL address',
        'The public address of your XRPL master key, the key that controls your account on the XRP '
            + 'Ledger. The key itself is printed in the document set; the address is safe to share.');
    container.appendChild(passwordRow.element);
    container.appendChild(supersecretRow.element);
    container.appendChild(xrplRow.element);
    const orgRow = config.includeOrgSupersecret
        ? credentialRow('Organization supersecret',
            'The organization\'s own master key: it encrypts the organization\'s keys and is never stored '
                + 'anywhere either. It is printed in the Organization Membership Kit; day to day, the '
                + 'organization is used through its delegates.')
        : null;
    if (orgRow) container.appendChild(orgRow.element);

    const pool = createEntropyPool();
    let running = false;

    container.addEventListener('pointermove', (e) => {
        // Coarse coordinates + timestamp — each event grows the pool.
        pool.add('move', Math.round(e.clientX) * 31 + Math.round(e.clientY) + performance.now());
    });

    const toggleBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Start',
    });

    function tick(): void {
        if (!running || !container.isConnected) { running = false; return; }
        pool.add('tick', performance.now());
        passwordRow.value.textContent = generatePassword();
        supersecretRow.value.textContent = generateSupersecret();
        // Address derivation is synchronous (noble ed25519 + ripemd160 +
        // base58check) and cheap enough for the 50 ms tick.
        xrplRow.value.textContent = deriveXrplAddress(generateXrplSeedEntropy());
        if (orgRow) orgRow.value.textContent = generateSupersecret();
        setTimeout(tick, 50);
    }

    toggleBtn.addEventListener('click', () => {
        if (!running) {
            running = true;
            pool.add('start', performance.now());
            toggleBtn.textContent = 'Stop';
            config.onReady(null);
            tick();
        } else {
            running = false;
            pool.add('stop', performance.now());
            toggleBtn.textContent = 'Start over';

            const seed = crypto.getRandomValues(new Uint8Array(32));
            const result: EntropyCeremonyResult = drawCeremonyCredentials(
                deriveCeremonyMaterial(seed, pool.digest()),
                {
                    includeOrgSupersecret: orgRow !== null,
                    ...(config.mintWalletKey ? { mintWalletKey: config.mintWalletKey } : {}),
                },
            );
            passwordRow.value.textContent = result.password;
            supersecretRow.value.textContent = result.supersecret;
            xrplRow.value.textContent = deriveXrplAddress(result.xrplSeedEntropy);
            if (orgRow && result.orgSupersecret) orgRow.value.textContent = result.orgSupersecret;
            config.onReady(result);
        }
    });
    container.appendChild(toggleBtn);

    // The wallet key is named only where this ceremony draws one.
    const keptKeys = config.mintWalletKey
        ? 'your XRPL master key and your Bitcash wallet key are'
        : 'your XRPL master key is';
    container.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'The values freeze when you press Stop and go straight to the printer. '
            + `The password and the supersecret are never stored anywhere; ${keptKeys} kept only in `
            + 'your own account, encrypted with the supersecret.',
    }));

    return container;

    function credentialRow(label: string, hint: string): { element: HTMLElement; value: HTMLElement } {
        const row = createElement('div', { className: 'cid-credential-display' });
        row.appendChild(createElement('div', { className: 'cid-key-field-label', textContent: label }));
        const value = createElement('div', {
            className: 'cid-credential-value cid-credential-value--mono',
            textContent: '—',
        });
        value.style.whiteSpace = 'pre-line';
        row.appendChild(value);
        row.appendChild(createElement('p', { className: 'cid-pane-note cid-credential-hint', textContent: hint }));
        return { element: row, value };
    }
}
